import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { MoreHorizontal, Share2, Pin, PinOff, Unlink, Trash2, Radio, Play, Loader2, Flame } from "lucide-react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { useApp } from "@/context/AppContext";
import { resolveMediaUrl } from "@/components/FileUpload";
import { liveTitle } from "@/lib/liveTitle";
import { shareLinkCard } from "@/lib/nativeShare";
import { publicOrigin } from "@/lib/publicOrigin";
import { compactNumber } from "@/lib/formatNumber";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { DropdownMenu, DropdownMenuTrigger, DropdownMenuContent, DropdownMenuItem, DropdownMenuSeparator } from "@/components/ui/dropdown-menu";

// Durée « 51:50 » ou « 1:02:10 ».
export const fmtDur = (s) => {
  if (!s || s <= 0) return "";
  const h = Math.floor(s / 3600), m = Math.floor((s % 3600) / 60), sec = Math.floor(s % 60);
  return h ? `${h}:${String(m).padStart(2, "0")}:${String(sec).padStart(2, "0")}` : `${m}:${String(sec).padStart(2, "0")}`;
};

export const livePath = (l) => (l.status === "live" ? `/live?watch=${l.id}` : (l.replay_post_id ? `/post/${l.replay_post_id}` : `/replay/${l.id}`));

// Actions partagées (page d'église + page Rediffusions) : épingler, détacher, supprimer → corbeille.
export const useChurchLiveActions = ({ churchId, t, onChanged }) => {
  const togglePin = async (l) => {
    try {
      await api.post(`/churches/${churchId}/lives/${l.id}/pin`, { pinned: !l.church_pinned });
      toast.success(!l.church_pinned ? t("church.pinned", "Direct épinglé en haut 📌") : t("church.unpinned", "Direct désépinglé"));
      onChanged?.("pin", l);
    } catch (e) { toast.error(e?.friendlyMessage || t("common.error", "Erreur")); }
  };
  const detachLive = async (l) => {
    if (!window.confirm(t("church.detach_confirm", "Retirer ce direct de la page de l'église ?"))) return;
    try {
      await api.post(`/churches/${churchId}/detach-live`, { live_id: l.id });
      toast.success(t("church.detached", "Direct détaché de la page ✅"));
      onChanged?.("remove", l);
    } catch (e) { toast.error(e?.friendlyMessage || t("common.error", "Erreur")); }
  };
  const deleteLive = async (l) => {
    await api.delete(`/live/${l.id}`);
    toast.success(t("church.replay_trashed", "Déplacé vers la corbeille de « Mes lives » 🗑️"));
    onChanged?.("remove", l);
  };
  return { togglePin, detachLive, deleteLive };
};

/** Carte façon YouTube : vignette 16:9 (badge LIVE/REPLAY, durée), puis titre sur 2 lignes + date · vues, menu ⋯.
 *  `rank` (1..3) : tri « Les plus vues » → pastille N°1/2/3 en bas à gauche de la vignette. */
export const ChurchReplayCard = ({ live: l, church, canEdit, actions, rank = 0 }) => {
  const { t, lang, user } = useApp();
  const nav = useNavigate();
  const [confirm, setConfirm] = useState(false);
  const [busy, setBusy] = useState(false);
  const title = liveTitle(l, t, { name: church?.name });
  const isLive = l.status === "live";
  const canDelete = !!user && (l.host_id === user.id || user.role === "super_admin");
  const poster = l.poster_url || l.thumbnail_url;
  const date = l.started_at ? new Date(l.started_at).toLocaleDateString(lang || "fr", { day: "numeric", month: "short", year: "numeric" }) : "";
  const views = isLive
    ? t("church.viewers_now", "{n} spectateur(s)").replace("{n}", compactNumber(l.peak_viewers || 0))
    : t("church.views_count", "{n} vue(s)").replace("{n}", compactNumber(l.replay_views || 0));
  const share = () => shareLinkCard({
    title, text: `${l.title ? church.name + " — " : ""}${title} 🙏`,
    url: `${publicOrigin()}${livePath(l)}`, copiedMessage: t("live.invite_copied", "Lien copié 🔗"),
  });
  const doDelete = async () => {
    setBusy(true);
    try { await actions.deleteLive(l); setConfirm(false); }
    catch (e) { toast.error(e?.friendlyMessage || t("common.error", "Erreur")); }
    setBusy(false);
  };

  return (
    <div className="group min-w-0" data-testid={`church-replay-card-${l.id}`}>
      <button type="button" onClick={() => nav(livePath(l))} className="relative block w-full aspect-video rounded-xl overflow-hidden text-left" style={{ background: "#0A0D0C" }} data-testid={`church-live-${l.id}`}>
        {poster ? (
          <img src={resolveMediaUrl(poster)} alt="" loading="lazy" className="absolute inset-0 w-full h-full object-cover transition-transform duration-300 group-hover:scale-[1.03]" />
        ) : (
          <div className="absolute inset-0 grid place-items-center opacity-40"><Radio size={28} className="text-white" /></div>
        )}
        <span className="absolute inset-0 grid place-items-center opacity-0 group-hover:opacity-100 transition-opacity" style={{ background: "rgba(0,0,0,0.25)" }}>
          <span className="h-12 w-12 rounded-full grid place-items-center bg-white/90 text-black"><Play size={20} className="ml-0.5" fill="currentColor" /></span>
        </span>
        {isLive ? (
          <span className="absolute top-2 left-2 text-[10px] font-bold uppercase text-white px-1.5 py-0.5 rounded flex items-center gap-1" style={{ background: "#D97757" }} data-testid={`church-live-badge-${l.id}`}>
            <span className="h-1.5 w-1.5 rounded-full bg-white animate-pulse" /> {t("church.live_badge", "Live")}
          </span>
        ) : (
          <span className="absolute top-2 left-2 text-[10px] font-bold uppercase text-white px-1.5 py-0.5 rounded" style={{ background: "rgba(0,0,0,0.6)" }}>{t("church.replay_badge", "Replay")}</span>
        )}
        {l.church_pinned && (
          <span className="absolute top-2 right-2 text-[10px] font-bold uppercase text-white px-1.5 py-0.5 rounded flex items-center gap-1" style={{ background: "var(--yalola-primary)" }} data-testid={`church-live-pinned-badge-${l.id}`}>
            <Pin size={9} /> {t("church.pinned_badge", "Épinglé")}
          </span>
        )}
        {!isLive && l.replay_duration > 0 && (
          <span className="absolute bottom-2 right-2 text-[11px] font-semibold text-white px-1.5 py-0.5 rounded tabular-nums" style={{ background: "rgba(0,0,0,0.75)" }} data-testid={`church-live-duration-${l.id}`}>{fmtDur(l.replay_duration)}</span>
        )}
        {rank > 0 && rank <= 3 && (
          <span className="absolute bottom-2 left-2 text-[11px] font-bold px-2 py-0.5 rounded-full flex items-center gap-1 shadow-sm" style={{ background: "var(--yalola-accent)", color: "#1F1A12" }} data-testid={`church-live-rank-${l.id}`}>
            <Flame size={11} fill="currentColor" /> {t("church.top_rank", "N°{n}").replace("{n}", String(rank))}
          </span>
        )}
      </button>
      <div className="flex items-start gap-1.5 mt-2">
        <button type="button" onClick={() => nav(livePath(l))} className="flex-1 min-w-0 text-left">
          <h3 className="text-sm font-semibold leading-snug line-clamp-2" title={title} data-testid={`church-live-title-${l.id}`}>{title}</h3>
          <p className="text-xs mt-0.5 truncate" style={{ color: "var(--yalola-text-muted)" }} data-testid={`church-live-meta-${l.id}`}>
            {date}{date ? " · " : ""}{views}
          </p>
        </button>
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <button type="button" className="h-8 w-8 -mr-1 shrink-0 rounded-full grid place-items-center opacity-70 hover:opacity-100 hover:bg-black/5 transition-colors outline-none" aria-label={t("church.more_actions", "Plus d'actions")} data-testid={`church-live-menu-${l.id}`}>
              <MoreHorizontal size={18} />
            </button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end" className="w-56" data-testid={`church-live-menu-content-${l.id}`}>
            <DropdownMenuItem onClick={share} data-testid={`church-live-invite-${l.id}`}><Share2 size={15} className="mr-2" /> {t("church.invite", "Inviter")}</DropdownMenuItem>
            {canEdit && (
              <DropdownMenuItem onClick={() => actions.togglePin(l)} data-testid={`church-live-pin-${l.id}`}>
                {l.church_pinned ? <PinOff size={15} className="mr-2" /> : <Pin size={15} className="mr-2" />}
                {l.church_pinned ? t("church.unpin_title", "Désépingler") : t("church.pin_title", "Épingler en haut")}
              </DropdownMenuItem>
            )}
            {canEdit && (
              <DropdownMenuItem onClick={() => actions.detachLive(l)} data-testid={`church-live-detach-${l.id}`}><Unlink size={15} className="mr-2" /> {t("church.detach_title", "Détacher de la page")}</DropdownMenuItem>
            )}
            {canDelete && !isLive && (
              <>
                <DropdownMenuSeparator />
                <DropdownMenuItem onClick={() => setConfirm(true)} className="text-red-600 focus:text-red-600" data-testid={`church-live-delete-${l.id}`}><Trash2 size={15} className="mr-2" /> {t("church.delete_replay", "Supprimer (corbeille)")}</DropdownMenuItem>
              </>
            )}
          </DropdownMenuContent>
        </DropdownMenu>
      </div>
      <Dialog open={confirm} onOpenChange={(o) => !o && !busy && setConfirm(false)}>
        <DialogContent className="z-[95]" data-testid={`church-live-delete-modal-${l.id}`}>
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2"><Trash2 size={18} style={{ color: "#dc2626" }} /> {t("mylives.delete_title", "Supprimer ce direct ?")}</DialogTitle>
          </DialogHeader>
          <p className="text-sm" style={{ color: "var(--yalola-text-muted)" }}>{t("church.delete_replay_desc", "Ce direct disparaît de la page de l'église, du fil et de « Mes lives ». Tu pourras le restaurer pendant 7 jours depuis « Mes lives › Corbeille ».")}</p>
          <DialogFooter>
            <Button variant="outline" onClick={() => setConfirm(false)} disabled={busy} data-testid={`church-live-delete-cancel-${l.id}`}>{t("common.cancel", "Annuler")}</Button>
            <Button onClick={doDelete} disabled={busy} className="text-white" style={{ background: "#dc2626" }} data-testid={`church-live-delete-confirm-${l.id}`}>
              {busy ? <Loader2 size={16} className="animate-spin" /> : t("common.delete", "Supprimer")}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};
